import addIcon from 'assets/icons/add.svg';
import editIcon from 'assets/icons/editIcon.svg';
import linkIcon from 'assets/icons/linkIcon.svg';
import { tmsAllowRoles } from 'data/facts/userRolesValidations';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { getRoundedValueForMarketShare } from 'utils/computation';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import MarketStrategyModal from './components/marketStrategyEditModal/MarketStrategyAddEditModal';
import MarketStrategyHistoryModal from './components/TMSHistoryModal/TMSHistoryModal';
import DrugMarketStrategySkeleton from './components/marketStrategySkeleton/MarketStrategySkeletonLoader';
import classes from './marketStrategy.module.scss';
import { useMarketStrategy } from './useMarketStrategy';

const Heading = ({
  heading,
  historyCLickHandler,
  tmsCalculationData,
}: {
  heading: string;
  historyCLickHandler: () => void;
  tmsCalculationData: number | null;
}) => {
  return (
    <div className={classes.tableHeader}>
      <div className={classes.leftSide}>{heading}</div>
      {tmsCalculationData !== null && (
        <div className={classes.averageTms}>Total TMS - {tmsCalculationData.toFixed(2)} %</div>
      )}
      <div onClick={historyCLickHandler} className={classes.rightSide}>
        <span>Historical / Future Values</span>
        <img src={linkIcon} alt="history" />
      </div>
    </div>
  );
};

const formatDateString = (dateString: string) => {
  const [year, month] = dateString.split('-');

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const formattedMonth = monthNames[parseInt(month) - 1];

  const lastTwoDigitsYear = year.slice(-2);

  return `${formattedMonth} ${lastTwoDigitsYear}`;
};

const MarketStrategy = () => {
  const {
    drugConceptsList,
    isMarketStrategyTableDataLoading,
    getRowDataByType,
    COTTypes,
    isMarketHistoryModalVisible,
    marketStrategyTableData,
    toggleMarketHistoryModalVisibility,
    marketStrategyModalVisibility,
    toggleMarketStrategyModalVisibility,
    setSelectedMarketStrategyIndexForEdit,
    directStrategyHistoryClickHandler,
    indirectStrategyHistoryClickHandler,
    isTmsAddModalVisible,
    setSelectedMarketStrategyIndexForAdd,
    toggleTMSAddVisibility,
    userModulesRoles,
    tmsCalculationData,
    isConceptsListLoading,
  } = useMarketStrategy();

  return (
    <div className={classes.marketStrategyContainer}>
      <div className={classes.heading}>
        <div>Market Strategy</div>
        <div>
          {tmsCalculationData && (
            <span>
              {isMarketStrategyTableDataLoading || marketStrategyTableData.length === 0
                ? ''
                : `Total TMS - ${(tmsCalculationData.overall * 100).toFixed(2)} %`}
            </span>
          )}
        </div>
      </div>
      {/* Market share table should be visible if any  marketShareStrategyTable Edit, or market history are closed and marketStrategyDataisLoading is false*/}
      {(!isMarketStrategyTableDataLoading && !isConceptsListLoading) ||
      marketStrategyModalVisibility ||
      isMarketHistoryModalVisible ? (
        <>
          {drugConceptsList.length > 0 && (
            <div className={classes.strategyTablesContainer}>
              <div className={classes.directStrategyTable}>
                <Heading
                  heading="Direct Strategy"
                  historyCLickHandler={directStrategyHistoryClickHandler}
                  tmsCalculationData={tmsCalculationData ? tmsCalculationData.direct * 100 : null}
                />
                <table className={classes.datatable}>
                  <thead>
                    <tr>
                      <th>COT</th>
                      <th>TMS {'(%)'}</th>
                      <th>Strategy</th>
                      <th>Time Period</th>

                      <th className={classes.editButtonColumn}>{''}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COTTypes.map((type, index) => {
                      const row = getRowDataByType(type, true);
                      return (
                        <tr key={index}>
                          <td>{type}</td>
                          <td>
                            {row ? getRoundedValueForMarketShare(row.TargetMarketShare) : '-'}
                          </td>
                          <td>{row ? row.TMSStrategyID.TMSStrategyCode : '-'}</td>
                          <td>
                            {row
                              ? `${formatDateString(
                                  row?.StartDate
                                )} - ${formatDateString(row?.EndDate)}`
                              : '-'}
                          </td>
                          <td className={classes.editButtonColumn}>
                            <div>
                              <Button
                                onClick={() =>
                                  setSelectedMarketStrategyIndexForEdit(index, true, type)
                                }
                                className={classes.editIconButton}
                                icon={
                                  <img
                                    src={addIcon}
                                    alt="edit"
                                    style={{ height: '12px', width: '12px' }}
                                  />
                                }
                                disabled={
                                  !checkIfFunctionalityEnabled(
                                    tmsAllowRoles.newTmsAddButton,
                                    userModulesRoles.facts,
                                    userModulesRoles
                                  )
                                }
                                rounded
                                text
                                aria-label="Filter"
                              />
                              <Button
                                disabled={
                                  (row?.COT ? false : true) ||
                                  !checkIfFunctionalityEnabled(
                                    tmsAllowRoles.tmsRowEditButton,
                                    userModulesRoles.facts,
                                    userModulesRoles
                                  )
                                }
                                onClick={() =>
                                  setSelectedMarketStrategyIndexForAdd(index, true, type)
                                }
                                className={classes.editIconButton}
                                icon={
                                  <img
                                    src={editIcon}
                                    alt="edit"
                                    style={{ height: '12px', width: '12px' }}
                                  />
                                }
                                rounded
                                text
                                aria-label="Filter"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className={classes.indirectStrategyTable}>
                <Heading
                  heading="Indirect Strategy"
                  historyCLickHandler={indirectStrategyHistoryClickHandler}
                  tmsCalculationData={tmsCalculationData ? tmsCalculationData.indirect * 100 : null}
                />

                <table className={classes.datatable}>
                  <thead>
                    <tr>
                      <th>COT</th>
                      <th>TMS {'(%)'}</th>
                      <th>Strategy</th>
                      <th>Time Period</th>
                      <th className={classes.editButtonColumn}>{''}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COTTypes.map((type, index) => {
                      const row = getRowDataByType(type, false);
                      return (
                        <tr key={index}>
                          <td>{type}</td>
                          <td>
                            {row ? getRoundedValueForMarketShare(row.TargetMarketShare) : '-'}
                          </td>
                          <td>{row ? row.TMSStrategyID.TMSStrategyCode : '-'}</td>
                          <td>
                            {row
                              ? `${formatDateString(
                                  row?.StartDate
                                )} - ${formatDateString(row?.EndDate)}`
                              : '-'}
                          </td>
                          <td className={classes.editButton}>
                            <div>
                              <Button
                                onClick={() =>
                                  setSelectedMarketStrategyIndexForEdit(index, false, type)
                                }
                                className={classes.editIconButton}
                                icon={
                                  <img
                                    src={addIcon}
                                    alt="edit"
                                    style={{ height: '12px', width: '12px' }}
                                  />
                                }
                                disabled={
                                  !checkIfFunctionalityEnabled(
                                    tmsAllowRoles.newTmsAddButton,
                                    userModulesRoles.facts,
                                    userModulesRoles
                                  )
                                }
                                rounded
                                text
                                aria-label="Filter"
                              />
                              <Button
                                disabled={
                                  (row?.COT ? false : true) ||
                                  !checkIfFunctionalityEnabled(
                                    tmsAllowRoles.newTmsAddButton,
                                    userModulesRoles.facts,
                                    userModulesRoles
                                  )
                                }
                                onClick={() =>
                                  setSelectedMarketStrategyIndexForAdd(index, false, type)
                                }
                                className={classes.editIconButton}
                                icon={
                                  <img
                                    src={editIcon}
                                    alt="edit"
                                    style={{ height: '12px', width: '12px' }}
                                  />
                                }
                                rounded
                                text
                                aria-label="Filter"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <DrugMarketStrategySkeleton />
      )}
      {marketStrategyModalVisibility && (
        <MarketStrategyModal
          isEditPopUp={false}
          visibility={marketStrategyModalVisibility}
          toggleVisibility={toggleMarketStrategyModalVisibility}
        />
      )}
      {isMarketHistoryModalVisible && (
        <MarketStrategyHistoryModal
          visibility={isMarketHistoryModalVisible}
          toggleVisibility={toggleMarketHistoryModalVisibility}
        />
      )}
      {isTmsAddModalVisible && (
        <MarketStrategyModal
          isEditPopUp={true}
          visibility={isTmsAddModalVisible}
          toggleVisibility={toggleTMSAddVisibility}
        />
      )}
    </div>
  );
};

export default observer(MarketStrategy);
